import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/ui';
import PremiumName from '../components/ui/PremiumName';
import { updateUserProfile, getUserProfile } from '../services/profileService';
import { clearUserCache } from '../hooks/useUserData';
import { unblockUser } from '../services/userService';

const defaultPaymentMethods = () => ({
  cvu: { alias: '', cvu: '', active: false },
  paypal: { email: '', active: false },
  crypto: { address: '', label: '', active: false }
});

const SettingsPage = () => {
  const { userData } = useAuth();
  const [activeSection, setActiveSection] = useState('account');
  const [paymentMethods, setPaymentMethods] = useState(defaultPaymentMethods);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState(null);
  const [blockedResolved, setBlockedResolved] = useState([]);
  const [blockedLoading, setBlockedLoading] = useState(false);
  const [unblockingId, setUnblockingId] = useState(null);

  useEffect(() => {
    if (userData?.paymentMethods && typeof userData.paymentMethods === 'object') {
      setPaymentMethods(prev => ({
        ...defaultPaymentMethods(),
        ...userData.paymentMethods,
        cvu: { ...prev.cvu, ...userData.paymentMethods.cvu },
        paypal: { ...prev.paypal, ...userData.paymentMethods.paypal },
        crypto: { ...prev.crypto, ...userData.paymentMethods.crypto }
      }));
    }
  }, [userData?.id, userData?.paymentMethods]);

  const blockedIds = userData?.blocked || [];
  useEffect(() => {
    if (activeSection !== 'blocked' || blockedIds.length === 0) {
      setBlockedResolved([]);
      return;
    }
    let cancelled = false;
    setBlockedLoading(true);
    Promise.all(blockedIds.map(uid => getUserProfile(uid)))
      .then(results => {
        if (cancelled) return;
        const list = results.map((res, i) => {
          const uid = blockedIds[i];
          if (res.success && res.data) {
            const d = res.data;
            return {
              uid,
              displayName: d.displayName || 'Usuario',
              photoURL: d.photoURL || d.profileImage || null
            };
          }
          return { uid, displayName: 'Usuario no encontrado', photoURL: null };
        });
        setBlockedResolved(list);
      })
      .finally(() => {
        if (!cancelled) setBlockedLoading(false);
      });
    return () => { cancelled = true; };
  }, [activeSection, blockedIds.length, blockedIds.join(',')]);

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
        <div className="container-custom py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Configuración
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Administra tu cuenta y preferencias
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Sidebar de navegación */}
            <aside className="lg:col-span-1">
              <nav className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
                <button
                  onClick={() => setActiveSection('account')}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
                    activeSection === 'account'
                      ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 font-medium'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Cuenta
                </button>
                
                <button
                  onClick={() => setActiveSection('profile')}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
                    activeSection === 'profile'
                      ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 font-medium'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Perfil
                </button>

                <button
                  onClick={() => setActiveSection('privacy')}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
                    activeSection === 'privacy'
                      ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 font-medium'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Privacidad
                </button>

                <button
                  onClick={() => setActiveSection('notifications')}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
                    activeSection === 'notifications'
                      ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 font-medium'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  Notificaciones
                </button>

                <button
                  onClick={() => setActiveSection('payment')}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
                    activeSection === 'payment'
                      ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 font-medium'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2h-2m-4-1V7a2 2 0 012-2h2a2 2 0 012 2v1m-6 2a2 2 0 104 0m-4 0a2 2 0 110 4m-4 0a2 2 0 004 0m0 0a2 2 0 004 0" />
                  </svg>
                  Métodos de pago
                </button>

                <button
                  onClick={() => setActiveSection('blocked')}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
                    activeSection === 'blocked'
                      ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 font-medium'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM21 12h-6" />
                  </svg>
                  Usuarios bloqueados
                </button>
              </nav>
            </aside>

            {/* Contenido principal */}
            <main className="lg:col-span-3">
              {/* Sección de Cuenta */}
              {activeSection === 'account' && (
                <div className="space-y-6">
                  <section className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                      Información de la cuenta
                    </h2>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Email
                        </label>
                        <input
                          type="email"
                          value={userData?.email || ''}
                          disabled
                          className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          ID de usuario
                        </label>
                        <input
                          type="text"
                          value={userData?.id || ''}
                          disabled
                          className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white font-mono text-sm"
                        />
                      </div>
                    </div>
                  </section>

                  <section className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                      Cambiar contraseña
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      Para cambiar tu contraseña, cierra sesión y usa la opción "¿Olvidaste tu contraseña?" en la pantalla de inicio de sesión.
                    </p>
                    <Button variant="outline">
                      Ir a cambiar contraseña
                    </Button>
                  </section>
                </div>
              )}

              {/* Sección de Perfil */}
              {activeSection === 'profile' && (
                <section className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    Perfil público
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Para editar tu perfil, ve a tu página de perfil y haz clic en "Editar Perfil".
                  </p>
                  <Button variant="gradient" onClick={() => window.location.href = `/profile/${userData?.id}`}>
                    Ir a mi perfil
                  </Button>
                </section>
              )}

              {/* Sección de Privacidad */}
              {activeSection === 'privacy' && (
                <section className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    Privacidad y seguridad
                  </h2>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-slate-700">
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white">
                          Perfil público
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Tu perfil es visible para todos los usuarios
                        </p>
                      </div>
                      <div className="text-sm text-indigo-600 dark:text-indigo-400 font-medium">
                        Público
                      </div>
                    </div>

                    <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-slate-700">
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white">
                          Mostrar email
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Tu email no es visible públicamente
                        </p>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                        Privado
                      </div>
                    </div>
                  </div>
                </section>
              )}

              {/* Sección de Notificaciones */}
              {activeSection === 'notifications' && (
                <section className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    Preferencias de notificaciones
                  </h2>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-slate-700">
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white">
                          Nuevos seguidores
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Recibe notificaciones cuando alguien te siga
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" defaultChecked />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-slate-700">
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white">
                          Likes en posts
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Recibe notificaciones cuando alguien le dé like a tus posts
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" defaultChecked />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-slate-700">
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white">
                          Comentarios
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Recibe notificaciones cuando alguien comente en tus posts
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" defaultChecked />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between py-3">
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white">
                          Solicitudes de servicio
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Recibe notificaciones sobre órdenes de servicios
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" defaultChecked />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>
                  </div>
                </section>
              )}

              {/* Sección Usuarios bloqueados */}
              {activeSection === 'blocked' && (
                <section className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    Usuarios bloqueados
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Puedes desbloquear a alguien desde aquí si no lo encuentras en la app.
                  </p>
                  {blockedIds.length === 0 ? (
                    <p className="text-gray-500 dark:text-gray-400">
                      No tienes usuarios bloqueados.
                    </p>
                  ) : blockedLoading ? (
                    <p className="text-gray-500 dark:text-gray-400">
                      Cargando lista...
                    </p>
                  ) : (
                    <ul className="space-y-3">
                      {blockedResolved.map(({ uid, displayName, photoURL, isPremium, premiumNameColor }) => (
                        <li
                          key={uid}
                          className="flex items-center justify-between gap-4 py-3 px-4 rounded-lg bg-gray-50 dark:bg-slate-700/50 border border-gray-200 dark:border-slate-600"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            {photoURL ? (
                              <img
                                src={photoURL}
                                alt=""
                                className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                onError={e => { e.target.style.display = 'none'; e.target.nextElementSibling?.classList.remove('hidden'); }}
                              />
                            ) : null}
                            <div className={`w-10 h-10 rounded-full bg-gray-300 dark:bg-slate-600 flex items-center justify-center flex-shrink-0 text-sm font-medium text-gray-600 dark:text-gray-300 ${photoURL ? 'hidden' : ''}`}>
                              {(displayName && displayName !== 'Usuario no encontrado') ? displayName.charAt(0).toUpperCase() : '?'}
                            </div>
                            <div className="min-w-0">
                              <PremiumName
                                name={displayName}
                                isPremium={isPremium}
                                premiumColor={premiumNameColor}
                                nameClassName="font-medium text-gray-900 dark:text-white truncate block"
                              />
                              <Link
                                to={`/profile/${uid}`}
                                className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline truncate block"
                              >
                                Ver perfil
                              </Link>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={unblockingId === uid}
                            onClick={async () => {
                              setUnblockingId(uid);
                              const result = await unblockUser(uid);
                              setUnblockingId(null);
                              if (result.success) {
                                setBlockedResolved(prev => prev.filter(u => u.uid !== uid));
                              }
                            }}
                          >
                            {unblockingId === uid ? '...' : 'Desbloquear'}
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              )}

              {/* Sección Métodos de pago */}
              {activeSection === 'payment' && (
                <section className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    Métodos de pago para recibir
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Los pagos por servicios son actualmente directos entre cliente y vendedor. En el futuro la plataforma podría ofrecer suscripción Premium o procesar pagos con comisión; se avisará con antelación.
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                    <Link to="/premium" className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium">
                      Ver Illustra Premium
                    </Link>
                  </p>

                  <div className="space-y-6">
                    {/* Alias + CVU */}
                    <div className="p-4 bg-gray-50 dark:bg-slate-700/50 rounded-xl space-y-3">
                      <h3 className="font-medium text-gray-900 dark:text-white">Alias + CVU (Argentina)</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Alias</label>
                          <input
                            type="text"
                            value={paymentMethods.cvu?.alias ?? ''}
                            onChange={(e) => setPaymentMethods(prev => ({
                              ...prev,
                              cvu: { ...prev.cvu, alias: e.target.value }
                            }))}
                            placeholder="mi.alias"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">CVU</label>
                          <input
                            type="text"
                            value={paymentMethods.cvu?.cvu ?? ''}
                            onChange={(e) => setPaymentMethods(prev => ({
                              ...prev,
                              cvu: { ...prev.cvu, cvu: e.target.value }
                            }))}
                            placeholder="0000003100012345678901"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm"
                          />
                        </div>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={paymentMethods.cvu?.active ?? false}
                          onChange={(e) => setPaymentMethods(prev => ({
                            ...prev,
                            cvu: { ...prev.cvu, active: e.target.checked }
                          }))}
                          className="rounded border-gray-300 dark:border-slate-600 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Usar para recibir pagos</span>
                      </label>
                    </div>

                    {/* PayPal */}
                    <div className="p-4 bg-gray-50 dark:bg-slate-700/50 rounded-xl space-y-3">
                      <h3 className="font-medium text-gray-900 dark:text-white">PayPal</h3>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Email</label>
                        <input
                          type="email"
                          value={paymentMethods.paypal?.email ?? ''}
                          onChange={(e) => setPaymentMethods(prev => ({
                            ...prev,
                            paypal: { ...prev.paypal, email: e.target.value }
                          }))}
                          placeholder="tu@email.com"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm"
                        />
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={paymentMethods.paypal?.active ?? false}
                          onChange={(e) => setPaymentMethods(prev => ({
                            ...prev,
                            paypal: { ...prev.paypal, active: e.target.checked }
                          }))}
                          className="rounded border-gray-300 dark:border-slate-600 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Usar para recibir pagos</span>
                      </label>
                    </div>

                    {/* Cripto */}
                    <div className="p-4 bg-gray-50 dark:bg-slate-700/50 rounded-xl space-y-3">
                      <h3 className="font-medium text-gray-900 dark:text-white">Cripto</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Etiqueta (opcional)</label>
                          <input
                            type="text"
                            value={paymentMethods.crypto?.label ?? ''}
                            onChange={(e) => setPaymentMethods(prev => ({
                              ...prev,
                              crypto: { ...prev.crypto, label: e.target.value }
                            }))}
                            placeholder="USDT, BTC..."
                            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Dirección</label>
                          <input
                            type="text"
                            value={paymentMethods.crypto?.address ?? ''}
                            onChange={(e) => setPaymentMethods(prev => ({
                              ...prev,
                              crypto: { ...prev.crypto, address: e.target.value }
                            }))}
                            placeholder="0x..."
                            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm"
                          />
                        </div>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={paymentMethods.crypto?.active ?? false}
                          onChange={(e) => setPaymentMethods(prev => ({
                            ...prev,
                            crypto: { ...prev.crypto, active: e.target.checked }
                          }))}
                          className="rounded border-gray-300 dark:border-slate-600 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Usar para recibir pagos</span>
                      </label>
                    </div>
                  </div>

                  {paymentMessage && (
                    <p className={`mt-4 text-sm ${paymentMessage.success ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {paymentMessage.text}
                    </p>
                  )}
                  <div className="mt-6">
                    <Button
                      variant="gradient"
                      disabled={paymentSaving}
                      onClick={async () => {
                        if (!userData?.id) return;
                        setPaymentSaving(true);
                        setPaymentMessage(null);
                        const result = await updateUserProfile(userData.id, { paymentMethods });
                        setPaymentSaving(false);
                        if (result.success) {
                          clearUserCache(userData.id);
                          setPaymentMessage({ success: true, text: 'Métodos de pago guardados correctamente.' });
                        } else {
                          setPaymentMessage({ success: false, text: result.error || 'Error al guardar.' });
                        }
                      }}
                    >
                      {paymentSaving ? 'Guardando...' : 'Guardar métodos de pago'}
                    </Button>
                  </div>
                </section>
              )}
            </main>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SettingsPage;
